/**
 * Popover - Click-triggered floating content
 *
 * Usage:
 * <Popover
 *   trigger={<Button>Show Info</Button>}
 *   content={
 *     <div className="p-4">
 *       <h3 className="font-medium">Details</h3>
 *       <p className="text-sm text-zinc-400">Additional information here.</p>
 *     </div>
 *   }
 * />
 */

import { useState, useRef, useEffect, useCallback } from 'react';

import { cn } from '../utils';

export interface PopoverProps {
  /** Element that triggers popover */
  trigger: React.ReactElement;
  /** Popover content */
  content: React.ReactNode;
  /** Popover alignment */
  align?: 'left' | 'right' | 'center';
  /** Additional classes for popover */
  className?: string;
}

export function Popover({ trigger, content, align = 'left', className }: PopoverProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [openUpward, setOpenUpward] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Calculate if popover should open upward based on available space
  const calculatePosition = useCallback(() => {
    if (!containerRef.current) return;

    const triggerRect = containerRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const spaceBelow = viewportHeight - triggerRect.bottom;
    const spaceAbove = triggerRect.top;
    const estimatedPopoverHeight = 300; // Approximate max height

    setOpenUpward(spaceBelow < estimatedPopoverHeight && spaceAbove > estimatedPopoverHeight);
  }, []);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.stopImmediatePropagation();
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    // Use capture phase so this fires before any bubble-phase listeners (e.g., sidebar toggle)
    document.addEventListener('keydown', handleEscape, { capture: true });
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape, { capture: true });
    };
  }, []);

  const alignClasses = {
    left: 'left-0',
    right: 'right-0',
    center: 'left-1/2 -translate-x-1/2',
  };

  const handleToggle = () => {
    if (!isOpen) {
      calculatePosition();
    }
    setIsOpen(!isOpen);
  };

  return (
    <div ref={containerRef} className="relative inline-block">
      <div onClick={handleToggle}>{trigger}</div>
      {isOpen && (
        <div
          className={cn(
            'absolute z-50 min-w-[200px] max-w-[calc(100vw-2rem)] rounded-lg border border-gray-200 bg-white shadow-xl dark:border-white/[0.06] dark:bg-[#18181b]',
            openUpward ? 'bottom-full mb-2' : 'top-full mt-2',
            alignClasses[align],
            className
          )}
        >
          {content}
        </div>
      )}
    </div>
  );
}
